'use client';

/**
 * Browser-side error reporting to PostHog error tracking.
 *
 * Scope is deliberately narrow: errors the USER experiences on the client — a
 * failed org creation, a declined card, a chat stream that died, a canvas that
 * wouldn't render. Server errors, route-handler failures and generic React
 * Query rejections are Sentry's job and are not reported here.
 *
 * `useErrorTracking` is the entry point for components. `capturePostHogError`
 * is the same thing for callers that aren't components — class error
 * boundaries, which can't use hooks.
 *
 * Every capture is fire-and-forget and cannot throw: reporting must never break
 * the flow it is reporting on, and a failed capture must never reach the screen.
 */

import { useCallback, useEffect } from 'react';
import posthog from 'posthog-js';

import { pubsubPublishPostHogException } from '@/actions/services/posthog/actions';
import { IS_POSTHOG_ENABLED } from '@/lib/posthog-enabled';
import { useStore } from '@/stores';
import type { PostHogErrorContextType, PostHogExceptionPayloadType } from '@/types/posthog.types';
import { PublishThirdPartyService } from '@/types/pubsub.types';

/** Identity attached to every capture, refreshed as the user/org changes. */
type ErrorIdentity = {
  user_email?: string;
  org_id?: string;
  org_name?: string;
};

/**
 * Module-level because class error boundaries sit outside the Zustand context
 * and still need the user on their events. `PostHogIdentify` keeps it in sync —
 * it is mounted app-wide inside the store provider.
 */
let identity: ErrorIdentity = {};

/**
 * Errors that are a normal part of using the product, not failures.
 *
 * Stopping a chat stream, navigating away from an in-flight request, and a
 * React Query call torn down on unmount all raise an abort. They are
 * indistinguishable from a genuine fault at the `catch` site, so they are
 * filtered here instead.
 */
function isIgnorableError(error: unknown): boolean {
  if (!error) return true;

  const name = (error as { name?: unknown })?.name;
  if (name === 'AbortError' || name === 'CanceledError') return true;

  // Axios stamps aborts with this code.
  if ((error as { code?: unknown })?.code === 'ERR_CANCELED') return true;

  const message = extractErrorMessage(error).toLowerCase();
  if (!message) return false;

  return (
    message.includes('aborted') ||
    message.includes('abortsignal') ||
    message.includes('cancelled by user') ||
    // Next.js throws these to drive redirect()/notFound() — control flow, not
    // an error.
    message.includes('next_redirect') ||
    message.includes('next_not_found')
  );
}

/** Best-effort human-readable message from anything throwable. */
export function extractErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;

  const candidate = error as { message?: unknown; detail?: unknown; error?: unknown } | null;
  if (candidate && typeof candidate.message === 'string') return candidate.message;
  if (candidate && typeof candidate.detail === 'string') return candidate.detail;
  if (candidate && typeof candidate.error === 'string') return candidate.error;
  return '';
}

/**
 * Produce the Error object actually sent to PostHog.
 *
 * PostHog groups `$exception` events into issues by error type + message, so
 * passing a raw axios rejection through would collapse every 500 in the app
 * into one issue titled "Request failed with status code 500" — useless to
 * triage. Where the message carries no feature information we mint an error
 * named after the source/action instead, keeping issues separated by the thing
 * that actually broke. The original is preserved on `cause`.
 *
 * Real `Error`s with their own meaningful message pass through untouched.
 */
function toReportableError(error: unknown, context: PostHogErrorContextType): Error {
  const label = context.error_action
    ? `${context.error_source}:${context.error_action}`
    : context.error_source;

  const message = extractErrorMessage(error);
  const isGenericAxiosMessage = /^request failed with status code \d+$/i.test(message);

  if (error instanceof Error && message && !isGenericAxiosMessage) return error;

  const synthetic = new Error(message ? `${label} — ${message}` : label);
  // The name becomes the issue's "type" column in PostHog.
  synthetic.name = 'StriqueHandledError';
  if (error !== undefined && error !== null) {
    (synthetic as Error & { cause?: unknown }).cause = error;
  }
  // Prefer the original stack: ours would just point back into this file.
  if (error instanceof Error && error.stack) synthetic.stack = error.stack;
  return synthetic;
}

/** HTTP status from an axios rejection, a fetch Response-like, or our own shape. */
export function extractStatusCode(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;

  const err = error as { status?: unknown; statusCode?: unknown; response?: { status?: unknown } };

  for (const value of [err.response?.status, err.status, err.statusCode]) {
    if (typeof value === 'number' && value >= 100 && value < 600) return value;
  }
  return undefined;
}

/**
 * Canonical form for `integration_type`.
 *
 * The same platform reaches this function three ways — a kebab-case route
 * (`google-ads`), a display name (`Google Ads`) and a PlatformType enum
 * (`GOOGLE_ADS`) — and each would be its own bar in the PostHog breakdown.
 * Normalising here rather than at 25 call sites means a new one can't
 * reintroduce the split.
 */
function normalizeIntegrationType(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

/** Drop keys whose value is `undefined` so events never carry empty properties. */
function withoutEmptyValues(properties: Record<string, unknown>): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined && value !== null && value !== '') cleaned[key] = value;
  }
  return cleaned;
}

/**
 * Suppress a failure that is already on its way.
 *
 * Publishing is now a server action call plus a Pub/Sub publish, where it used
 * to be a browser beacon the SDK batched for us. That makes a component stuck in
 * a render/retry loop expensive: without this, one broken feature becomes a
 * request storm against our own server. The window is short enough that a user
 * genuinely retrying an action still gets each attempt recorded.
 */
const DUPLICATE_WINDOW_MS = 10_000;
const recentlyPublished = new Map<string, number>();

/** Exported for tests — a fresh page load gets a fresh map in the app. */
export function resetErrorTrackingDedupe(): void {
  recentlyPublished.clear();
}

function isDuplicateOfRecent(key: string): boolean {
  const now = Date.now();
  const lastSeen = recentlyPublished.get(key);
  if (lastSeen !== undefined && now - lastSeen < DUPLICATE_WINDOW_MS) return true;

  recentlyPublished.set(key, now);
  // Prune expired keys so a long session can't grow the map without bound.
  if (recentlyPublished.size > 100) {
    for (const [entry, seenAt] of recentlyPublished) {
      if (now - seenAt >= DUPLICATE_WINDOW_MS) recentlyPublished.delete(entry);
    }
  }
  return false;
}

/**
 * Read the browser SDK's own ids, when it has loaded.
 *
 * `distinct_id` is the important one: it is what ties this exception to the
 * same PostHog person as that session's analytics events. `PostHogIdentify`
 * sets it via `posthog.identify(user.id)` — the Clerk user id, the same value
 * the publisher falls back to server-side — and the SDK persists it in
 * localStorage + cookie for the rest of the session. Before init (or on a route
 * where `PostHogIdentify` never mounts, i.e. anything outside /app) it is the
 * anonymous id, which `identify` later merges into the real person.
 */
function readPostHogSessionIds(): { distinctId?: string; sessionId?: string } {
  if (!posthog.__loaded) return {};
  return {
    distinctId: posthog.get_distinct_id() || undefined,
    sessionId: posthog.get_session_id() || undefined
  };
}

/**
 * Report a caught error to PostHog error tracking with user + org context.
 *
 * Delivery follows the same path as an analytics event (see
 * `useStriqueAnalytics.trackPostHogEvent`): published to Pub/Sub by a server
 * action, consumed by /api/services/third-parties, and sent to PostHog with
 * posthog-node — never straight from the browser.
 *
 *   capturePostHogError  ← you are here
 *     → pubsubPublishPostHogException          (server action)
 *       → Pub/Sub `THIRD_PARTY_EVENT_TOPIC`
 *         → POST /api/services/third-parties   (push consumer)
 *           → trackPostHogExceptionAction      → posthog-node
 *
 * Safe to call from anywhere on the client, and safe to call when PostHog is
 * switched off: it no-ops on the server, no-ops when IS_POSTHOG_ENABLED is
 * false, and swallows its own failures. Returns immediately — the publish is
 * deliberately NOT awaited, so a slow or failing Pub/Sub can never hold up the
 * flow that raised the error.
 */
export function capturePostHogError(error: unknown, context: PostHogErrorContextType): void {
  try {
    if (typeof window === 'undefined') return;
    if (!IS_POSTHOG_ENABLED) return;
    if (isIgnorableError(error)) return;

    const reportable = toReportableError(error, context);
    if (
      isDuplicateOfRecent(
        `${context.error_source}:${context.error_action ?? ''}:${reportable.message}`
      )
    ) {
      return;
    }

    const { distinctId, sessionId } = readPostHogSessionIds();

    const properties = withoutEmptyValues({
      ...identity,
      ...context,
      error_message: context.error_message ?? extractErrorMessage(error),
      // Filled in from the error itself so every axios-backed capture carries
      // it without 25 call sites having to dig it out.
      status_code: context.status_code ?? extractStatusCode(error),
      ...(context.integration_type
        ? { integration_type: normalizeIntegrationType(context.integration_type) }
        : null),
      $session_id: sessionId,
      $current_url: window.location.href
    }) as PostHogExceptionPayloadType['properties'];

    // Not awaited, and `.catch` rather than a bare floating promise: an
    // unhandled rejection here would be picked up by `capture_unhandled_
    // rejections` and reported as a fresh error, which is a loop.
    void pubsubPublishPostHogException({
      service: PublishThirdPartyService.POSTHOG_EXCEPTION,
      payload: {
        ...(distinctId ? { distinctId } : null),
        // Flattened: an Error does not survive the JSON hop through Pub/Sub.
        error: {
          name: reportable.name,
          message: reportable.message,
          ...(reportable.stack ? { stack: reportable.stack } : null)
        },
        properties
      }
    }).catch((publishFailure) => {
      console.error('[posthog] failed to publish error event', publishFailure);
    });
  } catch (captureFailure) {
    // Reporting failing is not the user's problem — log and carry on.
    console.error('[posthog] failed to capture error', captureFailure);
  }
}

/**
 * Report caught errors to PostHog error tracking.
 *
 * Unhandled errors and promise rejections are captured automatically by
 * `capture_exceptions` in the PostHog provider — use this for errors we handle
 * ourselves so the `$exception` carries user + org context and a named source.
 *
 * `captureError` is stable for the life of the component, so effects and
 * callbacks can depend on it honestly instead of silencing exhaustive-deps.
 */
function useErrorTracking() {
  const captureError = useCallback(
    (error: unknown, context: PostHogErrorContextType) => capturePostHogError(error, context),
    []
  );

  return { captureError };
}

/**
 * Publish the current user/org into the identity that `capturePostHogError`
 * reads, so every capture carries it — including the ones raised outside React.
 *
 * Mounted once, app-wide, from `PostHogIdentify`.
 */
function useRegisterErrorIdentity() {
  const user = useStore((state) => state.user);
  const currentStore = useStore((state) => state.currentStore);

  const userEmail = user?.email;
  const orgId = currentStore?.striqueStore?.id;
  const orgName = currentStore?.striqueStore?.name;

  useEffect(() => {
    identity = {
      ...(userEmail ? { user_email: userEmail } : null),
      ...(orgId ? { org_id: orgId } : null),
      ...(orgName ? { org_name: orgName } : null)
    };

    // Cleared on unmount, not just overwritten on the next run. `PostHogIdentify`
    // only mounts under /app, and the module outlives it: a client-side
    // navigation to an auth or not-found route — or a sign-out — would
    // otherwise leave the previous user's email and org attached to every
    // later capture, misattributing them to a person who has left.
    return () => {
      identity = {};
    };
  }, [userEmail, orgId, orgName]);
}

export { useErrorTracking, useRegisterErrorIdentity };
